import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { DisplayStructure } from '../../shared/models/displayStructure';
import { StructureService } from '../../shared/services/structure.service';
import { SortDirection, SortEvent } from '../../shared/sortable/sort-event';
import { SortableDirective } from '../../shared/sortable/sortable.directive';
import { ToastService } from '../../shared/toast/toast.service';
import { StructureActionType } from './structure-action.type';
import { StructureDetailsComponent } from './structure-details/structure-details.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StructureFilter, StructureFilterValue } from './structureFilter';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { Utils } from '../../shared/utils';

@Component({
  selector: 'app-admin-structures',
  templateUrl: './structures.component.html',
  standalone: false,
})
export class StructuresComponent implements OnInit, OnDestroy {
  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public structures: DisplayStructure[] = [];
  public filteredStructures: DisplayStructure[] = [];
  private structureActionSubscription: Subscription;
  public structureFilterFormGroup: FormGroup<StructureFilter>;
  private readonly LAST_SEARCH_STORAGE_KEY = 'structure_last_search';

  constructor(
    private readonly structuresService: StructureService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly formBuilder: FormBuilder,
    private readonly localStorageService: LocalStorageService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.initFilters();

    this.structureActionSubscription = this.structuresService.structureAction$.subscribe((res: { action: StructureActionType }) => {
      switch (res.action) {
        case 'created':
          this.toastService.show('La structure a été ajoutée avec succès.', 'success');
          break;
        case 'updated':
          this.toastService.show('La structure a été modifiée avec succès.', 'success');
          break;
        default:
          break;
      }
      this.update();
    });

    await this.update();

    this.submitFilter();
  }

  public ngOnDestroy(): void {
    this.structureActionSubscription.unsubscribe();
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.update(direction != '' ? column : undefined, direction == 'desc');
  }

  public openModal(structureId?: number): void {
    const modal: NgbModalRef = this.modalService.open(StructureDetailsComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: StructureDetailsComponent = modal.componentInstance as StructureDetailsComponent;
    modalComponent.onInit(structureId);
  }

  private async update(column?: string, desc?: boolean): Promise<void> {
    this.structures = await this.structuresService.findAll(column, desc);
  }

  public submitFilter(): void {
    const filter = this.structureFilterFormGroup.value;
    this.filteredStructures = this.structures.filter(
      (structure) =>
        (!filter.name ||
          Utils.unaccent(filter.name)
            .toLocaleLowerCase()
            .split(/\s+/)
            .some((text) => Utils.unaccent(structure.label).toLocaleLowerCase().includes(text))) &&
        (!filter.siret || structure.SIRET?.replace(/\s/g, '').includes(filter.siret.replace(/\s/g, ''))) &&
        (filter.status === null || filter.status === structure.active) &&
        (filter.public === null || filter.public === structure.portalVisibility),
    );
    this.localStorageService.set(this.LAST_SEARCH_STORAGE_KEY, filter);
  }

  public initFilters(): void {
    let lastSearch: StructureFilterValue = this.localStorageService.get(this.LAST_SEARCH_STORAGE_KEY);
    if (lastSearch) {
      this.structureFilterFormGroup = this.formBuilder.group({
        name: [lastSearch.name],
        status: [lastSearch.status],
        siret: [lastSearch.siret],
        public: [lastSearch.public],
      });
    } else {
      this.structureFilterFormGroup = this.formBuilder.group({
        name: [''],
        status: [true],
        siret: [''],
        public: [null],
      });
    }
  }

  public resetFilters(): void {
    this.localStorageService.remove(this.LAST_SEARCH_STORAGE_KEY);
    this.initFilters();
    this.submitFilter();
  }
}
